import type { BadgeTone } from '@/components/ui/primitives'
import { toneForStatus } from '@/components/ui/primitives'
import { plainPreview } from '@/lib/markdown'
import { urlForKind, type UrlState } from '@/lib/urlState'
import type { Decision } from '@/features/decisions/types'
import { statusTone as taskTone, type Task } from '@/features/roadmap/types'
import { toneForDecision, type FieldDecision } from '@/features/fields/types'
import { finalFamily, finalName, finalSku, keepTone, type Product } from '@/features/products/types'
import { isBundle } from '@/features/products/bundles'
import { statusTone as ownerTone, type Owner } from '@/features/owners/types'
import type { StaffMember } from '@/features/orgchart/types'
import type { ItemKind, SearchRecord } from './types'

/**
 * Turning each collection into rows the search can rank.
 *
 * The rule for what goes where: `title` is the sentence somebody would recognise the thing
 * by, `subtitle` is how they tell two of them apart — the id, the object, the SKU, the
 * person — and `body` is everything else they might half-remember. A field nobody has
 * answered is found by its API name; a product by the SKU on the invoice rather than the
 * one in Salesforce; a decision by the answer somebody wrote rather than the question
 * somebody asked.
 */

/** Joined for display, so a missing middle piece does not leave a stray separator. */
const line = (...parts: Array<string | number | null | undefined>) =>
  parts.map((p) => (p == null ? '' : String(p).trim())).filter(Boolean).join(' · ')

/** Joined for matching only. Markdown markers come out; the words stay. */
const text = (...parts: Array<string | null | undefined>) =>
  plainPreview(parts.filter(Boolean).join(' \n '))

/**
 * The matched-but-not-shown half of a record.
 *
 * Values and prose are kept apart on the way in, because the same character means
 * different things in each: an underscore in a note is emphasis and comes out, an
 * underscore in `Vendasta_Id__c` is part of the name somebody is searching for. Running
 * the preview over an API name turns it into `VendastaIdc`, which is findable by nobody.
 */
const searchBody = (
  values: Array<string | null | undefined>,
  prose: Array<string | null | undefined>,
) => [values.filter(Boolean).join(' '), text(...prose)].filter(Boolean).join(' ')

export const decisionRecords = (rows: Decision[]): SearchRecord[] =>
  rows.map((d) => ({
    key: `decision:${d.decisionId}`,
    kind: 'decision',
    targetId: d.decisionId,
    title: plainPreview(d.question) || d.decisionId,
    subtitle: line(d.decisionId, d.workstream, d.owner),
    body: searchBody(
      [d.owner, d.workstream, d.priority, d.targetDate, d.source],
      [d.finalDecision, d.whyItMatters, d.recommendedStart, d.dependencies],
    ),
    status: d.status,
    tone: toneForStatus(d.status),
  }))

export const taskRecords = (rows: Task[]): SearchRecord[] =>
  rows.map((t) => ({
    key: `task:${t.id}`,
    kind: 'task',
    targetId: t.id,
    title: plainPreview(t.task) || t.id,
    subtitle: line(t.id, t.phase ? `Phase ${t.phase}` : '', t.owner),
    body: searchBody([t.owner, t.due, t.phase], [t.notes]),
    status: t.status,
    tone: taskTone(t.status) as BadgeTone,
  }))

export const fieldRecords = (rows: FieldDecision[]): SearchRecord[] =>
  rows.map((f) => ({
    key: `field:${f.id}`,
    kind: 'field',
    targetId: f.id,
    title: f.fieldName || f.apiName || f.id,
    subtitle: line(f.object, f.category, f.kind),
    // The API name is what somebody has open in Salesforce, and it is rarely the label —
    // and once the field is built, the one they have open is the new org's.
    body: searchBody(
      [f.apiName, f.id, f.builtAs, f.choices, f.usedIn.join(' ')],
      [f.suggestion, f.notes, f.ifChanging, f.usedInSummary],
    ),
    status: f.decision || 'Not decided',
    tone: toneForDecision(f.decision) as BadgeTone,
  }))

export const productRecords = (rows: Product[]): SearchRecord[] =>
  rows.map((p) => ({
    key: `product:${p.id}`,
    kind: 'product',
    targetId: p.id,
    // Found and shown by the name it will have; still findable by the one it has today,
    // because that is the name on last year's invoice.
    title: finalName(p) || finalSku(p) || p.id,
    subtitle: line(finalSku(p), finalFamily(p), isBundle(p) ? 'Bundle' : ''),
    body: searchBody(
      [p.vendastaSku, p.sku, p.name, p.family, p.billing, p.listPrice, p.setupFee],
      [p.notes, p.activity, p.purpose, p.benefits],
    ),
    status: p.keep || 'Not decided',
    tone: keepTone(p.keep) as BadgeTone,
  }))

export const ownerRecords = (rows: Owner[]): SearchRecord[] =>
  rows.map((o) => ({
    key: `owner:${o.id}`,
    kind: 'owner',
    targetId: o.id,
    title: o.role,
    // An unowned role is the one most worth finding, so it says so rather than showing a gap.
    subtitle: line(o.name || 'Nobody named yet', o.gate ? `Gate ${o.gate}` : ''),
    body: searchBody(
      [o.email, o.timeEstimate, o.backupFor],
      [o.whatTheyDo, o.notes, o.notable],
    ),
    status: o.status,
    tone: ownerTone(o.status) as BadgeTone,
  }))

/**
 * One person on the staff roster, as something findable from anywhere in the app — not
 * only from the roster's own search box.
 *
 * `title`/`subtitle` are `name`/`position`, the same pair the roster's own header row
 * leads with. `body` carries the rest of what `matchesStaff` (the roster's own search
 * predicate, in `StaffRosterTable.tsx`) checks — email, team, both usernames, the persona
 * group — so a person findable there by "Clara Sheller" (Clarissa Bush's legacy username)
 * is findable the same way from the command palette. The two are not the same function —
 * this builds a Fuse-ranked field, that is an exact substring test — but they are meant to
 * agree on *which* fields count, so the search does not surprise someone who already knows
 * the roster's own box works by legacy username. Status and tone are left blank: a
 * person's `status` (Active / Not yet placed / Not carried across) has its own tone
 * mapping today only as a private constant inside `StaffDrawer.tsx`, and exporting one
 * just for a search badge is more than this task asked for.
 */
export const staffRecords = (rows: StaffMember[]): SearchRecord[] =>
  rows.map((s) => ({
    key: `staff:${s.id}`,
    kind: 'staff',
    targetId: s.id,
    title: s.name,
    subtitle: s.position,
    body: searchBody(
      [s.email, s.team, s.legacyUsername, s.newOrgUsername, s.personaGroup],
      [],
    ),
    status: '',
    tone: 'neutral',
  }))

/**
 * One comment, as something findable.
 *
 * A comment has no page of its own, so it carries the id of the item it was written on
 * and opens that item's discussion. `parents` is the index that has already been built
 * from the collections, used to say *which* item on the row — a comment that only says
 * who wrote it is most of the way to useless. When the parent is not in there, the id is
 * shown rather than the comment being dropped: a role that cannot open a tab never loads
 * it, and a result with a thin subtitle beats no result at all.
 */
export type CommentRow = {
  id: string
  parentKind: ItemKind
  parentId: string
  body: string
  byName: string | null
  byEmail: string
  at?: { seconds: number } | null
}

export const commentRecords = (
  rows: CommentRow[],
  parents: Map<string, SearchRecord>,
): SearchRecord[] =>
  rows.map((c) => {
    const parent = parents.get(`${c.parentKind}:${c.parentId}`)
    const who = c.byName ?? c.byEmail
    return {
      // The same comment id can exist under two different items, so the parent is part
      // of the key.
      key: `comment:${c.parentKind}:${c.parentId}:${c.id}`,
      kind: 'comment' as const,
      parentKind: c.parentKind,
      // Open the parent, addressed the way its own tab addresses it.
      targetId: parent?.targetId ?? c.parentId,
      title: plainPreview(c.body),
      subtitle: line(who, `on ${parent?.title ?? c.parentId}`),
      // The address as well as the name: "who said something about this" is asked with
      // whichever of the two the person happens to have.
      body: searchBody([c.byEmail], []),
      status: '',
      tone: 'neutral' as const,
    }
  })

/**
 * Where a result lives.
 *
 * Every other id is cleared on the way. `tabForUrl` reads any leftover one as its own tab,
 * so a stale `task` would put the previous drawer back the moment somebody pressed back.
 */
export function urlForRecord(record: SearchRecord): Partial<UrlState> {
  // A comment opens the item it was written on; there is nowhere else to send anyone.
  return urlForKind(record.parentKind ?? (record.kind as ItemKind), record.targetId)
}
